'use client';

import React, { useState } from 'react';
import LoginForm from './LoginForm';
import RegisterForm from './RegisterForm';
import OTPVerification from './OTPVerification';
import TrustBadges from './TrustBadges';
import BrandPanel from './BrandPanel';

export type AuthStep = 'login' | 'register' | 'otp-email' | 'otp-phone';

export default function SignUpLoginScreen() {
  const [activeTab, setActiveTab] = useState<'login' | 'register'>('login');
  const [authStep, setAuthStep] = useState<AuthStep>('login');
  const [registeredEmail, setRegisteredEmail] = useState('');

  const handleRegisterSuccess = (email: string) => {
    setRegisteredEmail(email);
    setAuthStep('otp-email');
  };

  const handleEmailOTPSuccess = () => {
    setAuthStep('otp-phone');
  };

  const handlePhoneOTPSuccess = () => {
    // After full verification, redirect to profile onboarding
    window.location.href = '/onboarding';
  };

  const handleLoginSuccess = () => {
    // Backend integration point: redirect to dashboard after login
    window.location.href = '/dashboard';
  };

  const isOTPStep = authStep === 'otp-email' || authStep === 'otp-phone';

  return (
    <div className="min-h-screen flex bg-background">
      {/* Left brand panel — hidden on mobile */}
      <BrandPanel />

      {/* Right form panel */}
      <div className="flex-1 flex flex-col justify-center items-center px-6 py-12 lg:px-16 xl:px-24 min-h-screen">
        <div className="w-full max-w-md fade-in">
          {/* Mobile logo */}
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <div className="w-10 h-10 rounded-xl brand-gradient flex items-center justify-center shadow-primary-custom">
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                <path d="M11 4C9 1.5 5 1.5 3 4C1 6.5 2 10 5 12L11 18L17 12C20 10 21 6.5 19 4C17 1.5 13 1.5 11 4Z" fill="white" fillOpacity="0.9"/>
              </svg>
            </div>
            <div>
              <span className="font-display font-semibold text-lg text-foreground">SAATH</span>
              <p className="text-2xs text-muted-foreground tracking-wide">Bihar & Jharkhand's matchmaking platform</p>
            </div>
          </div>

          {!isOTPStep ? (
            <>
              {/* Tab switcher */}
              <div className="bg-muted rounded-xl p-1 flex mb-8 relative">
                <div
                  className="absolute top-1 bottom-1 rounded-[10px] tab-active-indicator transition-all duration-300 ease-out shadow-sm"
                  style={{
                    width: 'calc(50% - 4px)',
                    left: activeTab === 'login' ? '4px' : 'calc(50%)',
                  }}
                />
                <button
                  onClick={() => { setActiveTab('login'); setAuthStep('login'); }}
                  className={`relative z-10 flex-1 py-2.5 text-sm font-semibold rounded-[10px] transition-colors duration-200 ${
                    activeTab === 'login' ? 'text-white' : 'text-muted-foreground hover:text-foreground'
                  }`}
                  aria-selected={activeTab === 'login'}
                  role="tab"
                >
                  Sign In
                </button>
                <button
                  onClick={() => { setActiveTab('register'); setAuthStep('register'); }}
                  className={`relative z-10 flex-1 py-2.5 text-sm font-semibold rounded-[10px] transition-colors duration-200 ${
                    activeTab === 'register' ? 'text-white' : 'text-muted-foreground hover:text-foreground'
                  }`}
                  aria-selected={activeTab === 'register'}
                  role="tab"
                >
                  Create Account
                </button>
              </div>

              {/* Form content */}
              <div className="fade-in" key={activeTab}>
                {activeTab === 'login' ? (
                  <LoginForm onSuccess={handleLoginSuccess} />
                ) : (
                  <RegisterForm onSuccess={handleRegisterSuccess} />
                )}
              </div>

              {/* Trust badges — mobile */}
              <div className="mt-8 lg:hidden">
                <TrustBadges compact />
              </div>
            </>
          ) : (
            <div className="fade-in">
              <OTPVerification
                step={authStep}
                email={registeredEmail}
                onEmailSuccess={handleEmailOTPSuccess}
                onPhoneSuccess={handlePhoneOTPSuccess}
                onBack={() => setAuthStep('register')}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}